import { components, useApi } from "@appretio/api";
import { ContextSelectedOptions } from "@appretio/context-selector";
import { useQuery } from "@tanstack/react-query";
import { TemporalContext } from "../../../components/temporalContextSelector/data/temporalContextOptions";
import { convertOptionsToApiFormat } from "../utils/convertToApiFormat";
import { rrpKpisQueryKeys } from "./queryKeys";

/**
 * InSeasonREviewDataProduct schema with RRP KPIs added.
 *
 * These are added via the aggregation keywords
 */
export type RrpMetricsGroup = {
  /**
   * Recommended Retail Price (RRP) in EUR.
   */
  style_recommended_retail_price_eur: number;
  /**
   * Total Sales (NIS3) in EUR.
   */
  nis3_sum: number;
  /**
   * Total Purchases (Time Purchase) in EUR.
   */
  time_purchase_sum: number;
  /**
   * Average Sellthrough (ST) in %.
   */
  st_avg: number | string;
} & components["schemas"]["InSeasonReviewDataProduct"];

/**
 * Custom hook to fetch RRP KPIs for In-Season Review Page.
 * Sales, Purchases, and Salesthrough grouped by Recommended Retail Price (RRP).
 */
export function useFetchRrpKPIs(
  contextOptions: ContextSelectedOptions,
  temporalOptions: TemporalContext,
  enabled: boolean = false
) {
  const apiClient = useApi();

  return useQuery({
    queryKey: rrpKpisQueryKeys.data({ contextOptions, temporalOptions }),
    queryFn: async () => {
      const options = convertOptionsToApiFormat(
        contextOptions,
        temporalOptions
      );

      try {
        const response = await apiClient.POST(
          "/v1/in-season/review/get-review-data",
          {
            body: {
              ...options,
              query_params: {
                offset: 0,
                group: {
                  columns: ["style_recommended_retail_price_eur"]
                },
                aggregates: [
                  {
                    column: "nis3",
                    function: "sum",
                    alias: "nis3_sum"
                  },
                  {
                    column: "time_purchase",
                    function: "sum",
                    alias: "time_purchase_sum"
                  },
                  {
                    column: "st",
                    function: "avg",
                    alias: "st_avg"
                  }
                ]
              }
            }
          }
        );

        return (response.data?.result as RrpMetricsGroup[]) ?? [];
      } catch (error) {
        console.error("Error fetching context options:", error);

        throw error; // Let TanStack Query handle the error state
      }
    },
    enabled, // Only fetch when enabled is true
    staleTime: 5 * 60 * 1000 // 5 minutes
  });
}
